/**
 * Schema definition for payslip events CSV.
 * Column names and field validators based on product example.
 */

import { z } from "zod";

/** Required column names in exact order (as in product CSV). */
export const REQUIRED_COLUMNS: readonly string[] = [
  "ID",
  "Country",
  "Company",
  "Payroll",
  "Payrun",
  "Pay Date",
  "Step",
  "Action",
  "Action Owner",
  "Due Date",
  "Status",
] as const;

export type RequiredColumnName = (typeof REQUIRED_COLUMNS)[number];

/** DD/MM/YYYY date format regex. */
const DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;

/** DD/MM/YYYY-DD/MM/YYYY date range format. */
const DATE_RANGE_PATTERN = /^\d{2}\/\d{2}\/\d{4}-\d{2}\/\d{2}\/\d{4}$/;

const dateString = z
  .string()
  .regex(DATE_PATTERN, "Date must be in DD/MM/YYYY format");

const dateRangeString = z
  .string()
  .regex(DATE_RANGE_PATTERN, "Payrun must be in DD/MM/YYYY-DD/MM/YYYY format");

/** Due Date is optional: empty, whitespace, or DD/MM/YYYY. */
const optionalDateString = z
  .union([z.string(), z.undefined()])
  .transform((s) => (s ?? "").trim())
  .pipe(
    z.union([
      z.literal(""),
      z.string().regex(DATE_PATTERN, "Due Date must be DD/MM/YYYY or empty"),
    ]),
  );

/** Status values (Payslip can send Overdue and Reverted in addition to workflow states). */
const STATUS_VALUES = [
  "Not started",
  "In progress",
  "Completed",
  "Overdue",
  "Reverted",
] as const;

const nonEmptyString = z.string().min(1, "Must not be empty");

const positiveIntegerString = z
  .string()
  .min(1, "ID is required")
  .refine(
    (s) => /^\d+$/.test(s) && Number.parseInt(s, 10) > 0,
    "ID must be a positive integer",
  );

/** Zod schema for a single payslip event row (by column name). */
export const PayslipEventRowSchema = z.object({
  ID: positiveIntegerString,
  Country: nonEmptyString,
  Company: nonEmptyString,
  Payroll: nonEmptyString,
  Payrun: dateRangeString,
  "Pay Date": dateString,
  Step: nonEmptyString,
  Action: nonEmptyString,
  "Action Owner": nonEmptyString,
  "Due Date": optionalDateString,
  Status: z.enum(STATUS_VALUES, {
    errorMap: () => ({
      message: `Status must be one of: ${STATUS_VALUES.join(", ")}`,
    }),
  }),
});

export type PayslipEventRow = z.infer<typeof PayslipEventRowSchema>;

/** Build a record from headers and cell values for a single row. */
export function rowToRecord(
  headers: string[],
  cells: string[],
): Record<string, string> {
  const record: Record<string, string> = {};
  headers.forEach((h, i) => {
    record[h] = cells[i] ?? "";
  });
  return record;
}
